import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * The panel's one and only MapLibre instance (§5.9).
 *
 * ⚠️ **Every map is constructed through this module, and `react-map-gl` is handed it via
 * `mapLib`.** Left alone, `react-map-gl` imports its own copy of `maplibre-gl`: two instances, two
 * registries. A custom protocol or an RTL plugin registered on this one is then **never invoked by
 * the map — with no error, no warning, and no network request**, which is indistinguishable from
 * working until somebody reads a label.
 *
 * ⚠️ **Arabic text on a map is not automatic on the web** (§5.9). Without `setRTLTextPlugin`
 * MapLibre lays Arabic out in raw order, unjoined and reversed — measured on the mission map, where
 * سكاكا rendered as «الكاكس» and every district name with it. The plugin is registered below, once
 * per page, before the first map is constructed.
 */
export const mapLib = maplibregl;

/**
 * ⚠️ **Registered at module scope, which is the only place it can be.**
 *
 * `setRTLTextPlugin` has to be called once per page and *before* the first map exists; MapLibre
 * refuses a second call, and a map already constructed never picks it up. Putting it in a component
 * effect would make correct Arabic depend on which screen an operator happened to open first.
 *
 * ⚠️ Served from our own origin (§12.12), from `public/vendor/` — see the README there for why it is
 * copied rather than imported.
 *
 * ⚠️ Not lazy. The third argument defaults to eager loading, and that is deliberate: lazily, the
 * first map renders its labels wrong and re-shapes them once the plugin arrives, which on a slow
 * connection is a control room reading reversed place names for a second or two.
 */
// ⚠️ BASE_URL, for the same reason as the logo — and here it costs more than an icon. Under
// `base: '/admin/'` a bare '/vendor/…' reaches Caddy's SPA fallback, which returns index.html with
// a 200; `importScripts` then fails inside the worker and the RTL plugin never loads. The symptom
// is not an error a user sees: it is Arabic place names on the control room's map rendered with
// their letters disconnected and their order reversed, on the one screen read under time pressure.
maplibregl.setRTLTextPlugin(`${import.meta.env.BASE_URL}vendor/mapbox-gl-rtl-text.min.js`, false);

/**
 * Where the region packs and the glyphs are served from. Empty means no basemap.
 *
 * ⚠️⚠️ **A same-origin PATH is accepted, and production uses one (`/tiles`, in `.env.production`).**
 *
 * The variable is read at BUILD time, and until 13 September 2026 every production bundle carried
 * `http://localhost:8080` — inherited from a developer's `.env.local`, which Vite reads on a
 * production build too. In an operator's browser that address is their own computer, so the mission
 * map drew the search area on a blank ground and nothing else, and it had never once shown a street
 * in production. A path resolved against the page's own origin cannot point at the wrong machine,
 * and it carries no domain into the repository.
 */
const configuredTiles = (import.meta.env['VITE_TILES_URL'] ?? '').replace(/\/$/, '');
const tiles = configuredTiles.startsWith('/')
  ? `${window.location.origin}${configuredTiles}`
  : configuredTiles;

/**
 * ⚠️ Matched character for character against the style's `text-font` and the directory the tile
 * server has under `fonts/`. A mismatch yields a 404 per range, which MapLibre answers by drawing
 * the map without labels and reporting nothing at all.
 */
const FONT_STACK = 'Noto Sans Regular';

/** The colours the basemap is drawn in, all of them theme tokens. */
export interface BasemapColours {
  ground: string;
  earth: string;
  wadi: string;
  road: string;
  text: string;
}

/**
 * The style under the mission's own geometry.
 *
 * ⚠️ **Served from our own origin, never a public tile provider.** §12.12 forbids the panel
 * fetching anything from a third party at runtime, and §14 serves region `.pmtiles` packs cut on a
 * workstation from our own TileServer — the same archives the app carries on the device.
 *
 * ⚠️ **No tile server configured is a supported state, not a failure.** The overlays that matter —
 * where the team has walked and where it has not — are GeoJSON from our own API, and they render
 * on a plain ground exactly as well. A panel that refused to draw them because a basemap was
 * missing would withhold the thing an operator actually came for.
 *
 * ⚠️ Four layers, purpose-built. Wadis are LINES in the water layer and not polygons — the
 * distinction that made them invisible in the field spike's first attempt, and a dry riverbed is
 * the most useful feature on this ground.
 */
export function basemapStyle(colours: BasemapColours): maplibregl.StyleSpecification {
  const ground: maplibregl.LayerSpecification = {
    id: 'ground',
    type: 'background',
    paint: { 'background-color': colours.ground },
  };

  if (!tiles) {
    return { version: 8, sources: {}, layers: [ground] };
  }

  return {
    version: 8,

    // Local glyphs. Pointed at a remote glyph server the map draws perfectly and omits every
    // Arabic label — which on a search map is most of what is read.
    glyphs: `${tiles}/fonts/{fontstack}/{range}.pbf`,

    sources: {
      // ⚠️⚠️ **The whole Kingdom, and it used to be a 50 km box around Sakaka.** The panel drew
      // streets for one town and blank ground for every search anywhere else — while the app,
      // which carries a pack per region, drew them perfectly. An operator in the control room
      // reading a search in Riyadh saw the sector outline on nothing. Replaced on 21 September 2026
      // by one national archive cut at zoom 15, served from our own TileServer (§12.12).
      //
      // ⚠️ The name must match the key in `infra/tileserver/config.json`: a missing source is not
      // an error on screen, it is a map with no streets and no message.
      basemap: { type: 'vector', url: `${tiles}/data/saudi.json` },
    },

    layers: [
      ground,
      {
        id: 'earth',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'earth',
        paint: { 'fill-color': colours.earth },
      },
      {
        id: 'wadi',
        type: 'line',
        source: 'basemap',
        'source-layer': 'water',
        paint: { 'line-color': colours.wadi, 'line-width': 1.6 },
      },
      {
        id: 'roads',
        type: 'line',
        source: 'basemap',
        'source-layer': 'roads',
        paint: { 'line-color': colours.road, 'line-width': 1.2 },
      },
      {
        id: 'places',
        type: 'symbol',
        source: 'basemap',
        'source-layer': 'places',
        layout: {
          // Arabic first: the control room and the rescuers say the same word on the radio, and a
          // transliteration on one screen and an Arabic name on the air are two different places.
          'text-field': ['coalesce', ['get', 'name:ar'], ['get', 'name']],
          'text-font': [FONT_STACK],
          'text-size': 12,
        },
        paint: {
          'text-color': colours.text,

          // A halo, because a label crossing the gap overlay is otherwise unreadable exactly where
          // the map is busiest.
          'text-halo-color': colours.ground,
          'text-halo-width': 1.6,
        },
      },
    ],
  };
}
